import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { AttendantCompanyTypeEnum } from 'constants/loanApplication';

import {
  selectFeeProviderCategoriesValues,
  selectFeeProviderOptions,
} from 'store/system/selectors';

import {
  buildManufacturedHomeContactAutofill,
  getFeeProviderOptionsByCategoryId,
  getManufacturedHomeContactCategoryIds,
  MANUFACTURED_HOME_CONTACT_CONFIGS,
} from '../Content.manufacturedHomeContacts.utils';
import { getClearFields } from '../Content.utils';

// Bundles the Broker/Dealer/Investor read model (closing companies + Fee Provider
// lookup options) with their lookup-change handlers, so Content.js only needs one
// hook call for the whole Manufactured Home Contacts section.
export const useManufacturedHomeContacts = ({ closingCompanies, setFormRef, setIsTouchedRef }) => {
  const feeProviderOptions = useSelector(selectFeeProviderOptions);
  const feeProviderCategories = useSelector(selectFeeProviderCategoriesValues);

  const brokerCompany = useMemo(
    () => closingCompanies?.find(c => c.closingCompanyType === AttendantCompanyTypeEnum.BROKER),
    [closingCompanies],
  );
  const dealerCompany = useMemo(
    () => closingCompanies?.find(c => c.closingCompanyType === AttendantCompanyTypeEnum.DEALER),
    [closingCompanies],
  );
  const investorCompany = useMemo(
    () => closingCompanies?.find(c => c.closingCompanyType === AttendantCompanyTypeEnum.INVESTOR),
    [closingCompanies],
  );

  const [hasBroker, setHasBroker] = useState(() => !!brokerCompany?.attendantCompanyId);
  const [hasDealer, setHasDealer] = useState(() => !!dealerCompany?.attendantCompanyId);
  const [hasInvestor, setHasInvestor] = useState(() => !!investorCompany?.attendantCompanyId);

  const hasStateMap = useMemo(
    () => ({
      [AttendantCompanyTypeEnum.BROKER]: setHasBroker,
      [AttendantCompanyTypeEnum.DEALER]: setHasDealer,
      [AttendantCompanyTypeEnum.INVESTOR]: setHasInvestor,
    }),
    [],
  );

  const categoryIds = useMemo(
    () => getManufacturedHomeContactCategoryIds(feeProviderCategories),
    [feeProviderCategories],
  );

  const optionsByType = useMemo(
    () =>
      MANUFACTURED_HOME_CONTACT_CONFIGS.reduce((acc, { categoryType }) => {
        acc[categoryType] = getFeeProviderOptionsByCategoryId(
          feeProviderOptions,
          categoryIds[categoryType],
        );

        return acc;
      }, {}),
    [feeProviderOptions, categoryIds],
  );

  const onContactChange = useCallback(
    (e, option, config) => {
      const fieldId = config.getFieldIds(config.prefix);
      const setHasContact = hasStateMap[config.categoryType];

      if (!option) {
        setIsTouchedRef.current(true);
        setHasContact(false);
        setFormRef.current(prev => ({ ...prev, ...getClearFields(fieldId) }));

        return;
      }

      setIsTouchedRef.current(true);
      setHasContact(true);
      setFormRef.current(prev => ({
        ...prev,
        ...buildManufacturedHomeContactAutofill({
          option,
          prefix: config.prefix,
          getFieldIds: config.getFieldIds,
        }),
      }));
    },
    [hasStateMap, setFormRef, setIsTouchedRef],
  );

  const { onBrokerChange, onDealerChange, onInvestorChange } = useMemo(
    () => ({
      onBrokerChange: (e, option) =>
        onContactChange(e, option, MANUFACTURED_HOME_CONTACT_CONFIGS[0]),
      onDealerChange: (e, option) =>
        onContactChange(e, option, MANUFACTURED_HOME_CONTACT_CONFIGS[1]),
      onInvestorChange: (e, option) =>
        onContactChange(e, option, MANUFACTURED_HOME_CONTACT_CONFIGS[2]),
    }),
    [onContactChange],
  );

  return {
    brokerCompany,
    dealerCompany,
    investorCompany,
    hasBroker,
    setHasBroker,
    hasDealer,
    setHasDealer,
    hasInvestor,
    setHasInvestor,
    brokerOptions: optionsByType[AttendantCompanyTypeEnum.BROKER] || [],
    dealerOptions: optionsByType[AttendantCompanyTypeEnum.DEALER] || [],
    investorOptions: optionsByType[AttendantCompanyTypeEnum.INVESTOR] || [],
    onBrokerChange,
    onDealerChange,
    onInvestorChange,
  };
};
